/**
 * 廣告解鎖統計（Phase 1）服務層
 *
 * 對應 functions/src/handlers/ad_unlock_stats.ts 的兩個 endpoint：
 *   - getAdUnlockStats   單劇單月 episode breakdown
 *   - listAdUnlockStats  批查多劇單月 totals（給列表頁避免 N+1）
 *
 * 設計同 revenue.ts：Bearer token、Cloud Functions HTTP、無 SDK callable
 */
import { getAuth } from "firebase/auth";
import { app } from "./init";

function getBaseUrl(): string {
  const projectId =
    app.options.projectId ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    "onreel-dev";
  return `https://asia-east1-${projectId}.cloudfunctions.net`;
}

async function getIdToken(): Promise<string> {
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error("用戶未登入");
  return user.getIdToken();
}

export interface AdUnlockEpisodeStat {
  episode_number: number;
  unlocked_count: number;
  /** Phase 1.5：按 platform 拆，Phase 3 AdMob 分潤公式會用 */
  unlocked_count_ios: number;
  unlocked_count_android: number;
  last_unlocked_at: number | null;
}

export interface AdUnlockStatsResponse {
  drama_id: string;
  period: string;
  episodes: AdUnlockEpisodeStat[];
  total_unlocked_count: number;
  total_unlocked_count_ios: number;
  total_unlocked_count_android: number;
}

/**
 * 取得單劇單月 episode breakdown
 *
 * @param dramaId drama_id
 * @param period  YYYY-MM；省略 = 當月（Asia/Taipei）
 */
export async function getAdUnlockStats(
  dramaId: string,
  period?: string,
): Promise<AdUnlockStatsResponse> {
  const token = await getIdToken();
  const url = new URL(`${getBaseUrl()}/getAdUnlockStats`);
  url.searchParams.set("drama_id", dramaId);
  if (period) url.searchParams.set("period", period);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export interface AdUnlockTotalsResponse {
  period: string;
  totals: Record<string, number>;
}

/**
 * 批查多劇單月 totals（給 DramaListPage 用）
 */
export async function listAdUnlockStats(
  dramaIds: string[],
  period?: string,
): Promise<AdUnlockTotalsResponse> {
  if (dramaIds.length === 0) {
    return { period: period || "", totals: {} };
  }
  const token = await getIdToken();
  const res = await fetch(`${getBaseUrl()}/listAdUnlockStats`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ drama_ids: dramaIds, period }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 取得當月（Asia/Taipei）"YYYY-MM"，給 caller 預設用
 */
export function currentPeriodTaipei(): string {
  const d = new Date(Date.now() + 8 * 60 * 60 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * 列出日期範圍涵蓋的所有月份（YYYY-MM）。
 *
 * 用於把 financial date range（任意起訖日）轉成 monthly 分桶 query：
 *   periodsForRange("2026-04-25", "2026-05-10") → ["2026-04", "2026-05"]
 *
 * 注意：startDate / endDate 為 YYYY-MM-DD local string，不考慮時區轉換
 * （Phase 1 stats 本來就按 Asia/Taipei 分桶，前端傳什麼月份就抓什麼月份）
 */
export function periodsForRange(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate) return [];
  const [sy, sm] = startDate.split("-").map(Number);
  const [ey, em] = endDate.split("-").map(Number);
  if (!sy || !sm || !ey || !em) return [];
  const periods: string[] = [];
  let y = sy;
  let m = sm;
  // 安全上限：避免錯誤輸入導致無限迴圈（10 年 = 120 個月）
  let safety = 0;
  while ((y < ey || (y === ey && m <= em)) && safety < 120) {
    periods.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
    safety++;
  }
  return periods;
}

/**
 * 取得多劇在日期範圍內的廣告解鎖總次數（跨月時自動加總涵蓋的整月）。
 *
 * 例：startDate=2026-04-25, endDate=2026-05-10 → 回傳「整個 4 月 + 整個 5 月」之和。
 * Caller 端 UI 需要加 disclaimer 告知用戶實際是按月加總，非日級精度。
 */
export async function getAdUnlockTotalsForRange(
  dramaIds: string[],
  startDate: string,
  endDate: string,
): Promise<{ periods: string[]; totals: Record<string, number> }> {
  if (dramaIds.length === 0) {
    return { periods: [], totals: {} };
  }
  const periods = periodsForRange(startDate, endDate);
  if (periods.length === 0) {
    return { periods: [], totals: {} };
  }
  const results = await Promise.all(
    periods.map((p) => listAdUnlockStats(dramaIds, p))
  );
  const totals: Record<string, number> = {};
  for (const r of results) {
    for (const [id, count] of Object.entries(r.totals)) {
      totals[id] = (totals[id] || 0) + count;
    }
  }
  return { periods, totals };
}
